import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { addDays, addMilliseconds, OwaDate } from 'owa-datetime';
import { owaDate, differenceInCalendarDays, isSameDay } from 'owa-datetime';
import { ColumnIndex } from '../interfaces/ColumnRange';
import { ColumnProvider } from '../interfaces/ColumnProvider';
import { ColumnWidthRatio } from '../interfaces/ColumnWidthRatio';
import { computed } from 'mobx';
import { DateRange, getDateRangeDayCount, isDateInDateRange } from 'owa-datetime-utils';
import { getDefaultColumnWidthRatios } from './getColumnWidthRatios';
import { getOffset } from './DateOffsetConversion';
import { LayoutInterval } from '../interfaces/LayoutInterval';
import { overlap } from './overlap';
import { shrink } from './shrink';
import { VisibleTime } from 'owa-calendar-surface-core';
import {
    DateRangeType,
    DayOfWeek,
} from 'office-ui-fabric-react/lib/utilities/dateValues/DateValues';

export class DatesColumnProvider implements ColumnProvider {
    /**
     * @param store the store containing the date range
     */
    constructor(
        private store: {
            dateRange: DateRange;
            dateRangeType: DateRangeType;
            userOptions: {
                workDays?: DayOfWeek[];
            };
        } & VisibleTime,
        private columnWidthStrategy: typeof getDefaultColumnWidthRatios = getDefaultColumnWidthRatios
    ) {
        this.store = store;
    }

    /**
     * Gets the actual models for the visible columns
     */
    @computed
    get columnModels(): OwaDate[] {
        return this.dateRangeArray.filter(date => this.dateIsVisible(date));
    }

    /**
     * Gets the % edges of each column relative to the total
     */
    @computed
    get columnLayouts(): ColumnWidthRatio[] {
        return this.columnWidthStrategy(this.columnModels);
    }

    @computed
    private get dateRangeArray(): OwaDate[] {
        let result = [];

        let datesCount = getDateRangeDayCount(this.store.dateRange);
        for (let i = 0; i <= datesCount; i++) {
            result.push(addDays(this.store.dateRange.start, i));
        }

        return result;
    }

    /**
     * Gets the count of the total number of visible columns
     */
    get columnCount() {
        return this.columnModels.length;
    }

    /**
     * Calculates list of column intervals for given model
     * @param model The model for calculating column intervals
     *
     * The layout interval represents in which column & vertical
     * offset the model should be shown in the grid. The columm
     * provider decides what a 'column' means - it can be a date,
     * a calendar, a person or any other interesting field.
     * The vertical offset represents the number of minutes
     * the model is from the start of the day represented by a column.
     *
     * NOTE: small date ranges (ex, less than 30 minutes) might result
     * in intervals that are larger than the original date range duration
     * so the interval will be visible in the UI with a reasonable size.
     */
    getColumnIntervals(model: CalendarEvent): LayoutInterval[] {
        let results = [];

        // Use dates in the same time zone as the grid
        const tz = this.store.dateRange.start.tz;
        const start = owaDate(tz, model.Start);
        const end = owaDate(tz, model.End);

        const itemRange: DateRange = { start: start, end: end };
        const columnInterval = { start: 0, end: this.store.dayLength };

        let columnStart = 0;

        this.columnModels.forEach(date => {
            if (isDateInDateRange(itemRange, date)) {
                let itemInterval = this.createItemInterval(start, end, date);
                itemInterval.end = Math.max(itemInterval.start + 30, itemInterval.end);
                if (overlap(columnInterval, itemInterval)) {
                    shrink(itemInterval, columnInterval);
                    results.push({
                        start: itemInterval.start + columnStart,
                        end: itemInterval.end + columnStart,
                        clippedLeft: !this.dateIsVisible(start),
                        clippedRight: !this.dateIsVisible(addMilliseconds(end, -1)),
                    });
                }
            }
            columnStart += this.store.dayLength;
        });

        return results;
    }

    /**
     * Gets the date represented by a given column.
     *
     * Each column can represent an arbitrary date and this
     * function should return the start of the day of the requested column.
     */
    getColumnDate(columnIndex: ColumnIndex): OwaDate {
        let date = this.columnModels[columnIndex];
        return date ? date : null; // return null if date is null or undefined
    }

    getColumnIndex(date: OwaDate): number {
        if (!isDateInDateRange(this.store.dateRange, date)) {
            return -1;
        }

        const dates = this.columnModels;
        const l = dates.length;
        for (let i = 0; i < l; i++) {
            if (isSameDay(date, dates[i])) {
                return i;
            }
        }

        return -1;
    }

    private dateIsVisible(day: OwaDate) {
        // the date range is for columns, which means the date range doesn't actually encompass everything loaded. The actual visible range
        // goes to the end of the last day, so we add a day to the end
        if (!isDateInDateRange(this.store.dateRange, day)) {
            return false;
        }

        if (this.store.dateRangeType == DateRangeType.WorkWeek) {
            return (
                !this.store.userOptions.workDays ||
                this.store.userOptions.workDays.includes(day.getDay())
            );
        }

        return true;
    }

    private createItemInterval(
        startDate: OwaDate,
        endDate: OwaDate,
        rangeStartDate: OwaDate
    ): LayoutInterval {
        return {
            start: getOffset(
                startDate,
                differenceInCalendarDays(startDate, rangeStartDate),
                this.store
            ),
            end: getOffset(endDate, differenceInCalendarDays(endDate, rangeStartDate), this.store),
        };
    }
}
